import React from "react";
import { Group, Stack, Text } from "@mantine/core";
import { Badge, SectionCard } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { ImportReport } from "@models/mappingConfig";
import { AdminTable, col } from "@components/AdminTable";
import { formatValue } from "./format";

type Sheet = ImportReport["sheets"][number];
type Skipped = Sheet["skipped"][number] & { sheet: string };
type Autofilled = ImportReport["autofilled"][number];

/** What an import read, skipped and filled in by itself. */
export const ImportReportView: React.FC<{ report: ImportReport }> = ({ report }) => {
  const { t } = useTranslation();
  const skipped: Skipped[] = report.sheets.flatMap((s) => s.skipped.map((row) => ({ ...row, sheet: s.name })));

  return (
    <SectionCard
      data-testid="import-report"
      title={
        <Group gap="xs">
          <Text fw={600}>{t("mappingConfig.importReport.title")}</Text>
          <Badge variant="light">{report.importer}</Badge>
          <Badge variant="light" color="gray">{t(`mappingConfig.import.modes.${report.mode}`, { defaultValue: report.mode })}</Badge>
        </Group>
      }
    >
      <Stack gap="md">
        <Text size="sm">
          {report.file_name} <Text span size="xs" c="dimmed" ff="monospace">sha256 {report.file_sha256.slice(0, 12)}</Text>
        </Text>
        <AdminTable<Sheet>
          columns={[
            col.text<Sheet>("name", t("mappingConfig.importReport.sheet"), (s) => s.name),
            col.number<Sheet>("rows", t("mappingConfig.importReport.rowsRead"), (s) => s.rows_read),
            col.number<Sheet>("items", t("mappingConfig.importReport.items"), (s) => s.items),
            col.number<Sheet>("skipped", t("mappingConfig.importReport.skipped"), (s) => s.skipped.length),
          ]}
          data={report.sheets}
          getRowKey={(s) => s.name}
        />
        {skipped.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600}>{t("mappingConfig.importReport.skippedRows", { count: skipped.length })}</Text>
            <AdminTable<Skipped>
              columns={[
                col.text<Skipped>("sheet", t("mappingConfig.importReport.sheet"), (r) => r.sheet, { width: "24%" }),
                col.number<Skipped>("row", t("mappingConfig.importReport.row"), (r) => r.row),
                col.text<Skipped>("reason", t("mappingConfig.importReport.reason"), (r) => r.reason, { width: "24%" }),
                col.text<Skipped>("text", t("mappingConfig.importReport.text"), (r) => r.text, { dimmed: true }),
              ]}
              data={skipped}
              getRowKey={(r) => `${r.sheet}-${r.row}`}
            />
          </Stack>
        )}
        {report.autofilled.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600}>{t("mappingConfig.importReport.autofilled", { count: report.autofilled.length })}</Text>
            <AdminTable<Autofilled>
              columns={[
                col.text<Autofilled>("key", t("mappingConfig.importReport.key"), (r) => String(r.key), { mono: true, width: 112 }),
                col.text<Autofilled>("field", t("mappingConfig.diff.field"), (r) => r.field, { mono: true, width: "24%" }),
                col.text<Autofilled>("value", t("mappingConfig.importReport.value"), (r) => formatValue(r.value)),
              ]}
              data={report.autofilled}
              getRowKey={(r) => `${String(r.key)}-${r.field}`}
            />
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );
};
